import { useI18nContext } from '@/i18n/i18n-react';
import type { RowErrorLike } from '@/lib/campaign-import/errors-csv';
import type { ImportRowField } from '@/lib/campaign-import/row-validation';

/**
 * Turns a row error code into text, reusing the messages of the manual campaign form
 * where they exist. `describe` gives "Field: reason".
 */
export function useImportErrorMessages(storeWebsiteDomain: string) {
  const { LL } = useI18nContext();

  const fieldLabel = (field: ImportRowField): string => {
    switch (field) {
      case 'name':
        return LL.campaignImport.errors.fieldLabels.name();
      case 'launchDateTime':
        return LL.campaignImport.errors.fieldLabels.launchDateTime();
      case 'notificationTitle':
        return LL.campaignImport.errors.fieldLabels.notificationTitle();
      case 'notificationBody':
        return LL.campaignImport.errors.fieldLabels.notificationBody();
      case 'notificationLink':
        return LL.campaignImport.errors.fieldLabels.notificationLink();
      case 'utmCampaign':
        return LL.campaignImport.errors.fieldLabels.utmCampaign();
    }
  };

  const reason = ({ field, code }: RowErrorLike): string => {
    switch (code) {
      case 'required':
        switch (field) {
          case 'name':
            return LL.createCampaign.validation.campaignNameRequired();
          case 'launchDateTime':
            return LL.createCampaign.validation.launchDateRequired();
          case 'notificationTitle':
            return LL.createCampaign.validation.notificationTitleRequired();
          case 'notificationBody':
            return LL.createCampaign.validation.notificationBodyRequired();
          case 'notificationLink':
            return LL.createCampaign.validation.notificationLinkRequired();
          case 'utmCampaign':
            return LL.common.validation.thisFieldIsRequired();
        }
        return LL.common.validation.thisFieldIsRequired();
      case 'websiteDomainNotConfigured':
        return LL.createCampaign.validation.websiteDomainNotConfigured();
      case 'linkNotFromStore':
      case 'linkInvalid':
        return LL.createCampaign.validation.linkMustStartWith({
          prefix: `https://${storeWebsiteDomain}`,
        });
      case 'dateInvalid':
        return LL.createCampaign.validation.validDateRequired();
      case 'dateInPast':
        return LL.createCampaign.validation.launchDateInPast();
      case 'dateTooFar':
        return LL.createCampaign.validation.launchDateTooFarInFuture();
      case 'timeInPast':
        return LL.createCampaign.validation.launchTimeInFuture();
      case 'utmEmpty':
        return LL.common.validation.thisFieldIsRequired();
      case 'utmDuplicateInFile':
        return LL.campaignImport.errors.utmDuplicateInFile();
      case 'utmAlreadyUsed':
        return LL.campaignImport.errors.utmAlreadyUsed();
    }
  };

  const describe = (error: RowErrorLike): string =>
    LL.campaignImport.errors.fieldWithReason({
      field: fieldLabel(error.field),
      reason: reason(error),
    });

  return { fieldLabel, reason, describe };
}
